import React from "react";
import { useShallow } from "zustand/react/shallow";
import { usePanelStore } from "../../stores/panelStore";
import { MainPanelDesktopPhysicsSection } from "./MainPanelPhysicsControls";
import {
  DevToolsSection,
  RobotModelSections,
  SceneSection,
  TrainingIkSection,
} from "./MainPanelSections";
import { useMainPanelMenuPanelSetterFactory } from "./useMainPanelOpenControls";
import { getPhysicsAuthoringLockMessage, usePhysicsAuthoringLock } from "../../utils/physicsGuards";

const MainPanelContent = React.memo(function MainPanelContent({
  isMobilePanelViewport,
  showDevTools,
  showRuntimeDiagnostics,
  showTrainingIkTools,
  showPerformancePanel,
  onTogglePerformancePanel,
  onStartStop,
  onPauseResume,
  onOpenPhysicsPanel,
  onOpenRuntimeControlsPanel,
  getPanelToggleButtonClass,
  closeMainPanelPopups,
}) {
  const {
    sceneGraphOpen,
    setSceneGraphOpen,
    filePanelOpen,
    setFilePanelOpen,
    animationPanelOpen,
    setAnimationPanelOpen,
    materialEditorPanelOpen,
    setMaterialEditorPanelOpen,
    lightsPanelOpen,
    setLightsPanelOpen,
    circleDetectionOpen,
    setCircleDetectionOpen,
    historyPanelOpen,
    setHistoryPanelOpen,
    runtimeDebugOpen,
    setRuntimeDebugOpen,
    measurementsOpen,
    setMeasurementsOpen,
    ikTargetPanelOpen,
    setIkTargetPanelOpen,
    objectLibraryOpen,
    setObjectLibraryOpen,
    actuatorControlsOpen,
    setActuatorControlsOpen,
    warmstartDatasetOpen,
    setWarmstartDatasetOpen,
    behaviorEditorOpen,
    setBehaviorEditorOpen,
    policyRolloutOpen,
    setPolicyRolloutOpen,
    trainingMetricsOpen,
    setTrainingMetricsOpen,
    trainingPipelineOpen,
    setTrainingPipelineOpen,
    octoTrainingOpen,
    setOctoTrainingOpen,
    meshEditorOpen,
    setMeshEditorOpen,
    sceneInventoryOpen,
    setSceneInventoryOpen,
    modelCheckOpen,
    setModelCheckOpen,
    massPropertiesOpen,
    setMassPropertiesOpen,
    terrainPanelOpen,
    setTerrainPanelOpen,
    xmlSourcePanelOpen,
    setXmlSourcePanelOpen,
    cameraProbeOpen,
    setCameraProbeOpen,
    sensorsOpen,
    setSensorsOpen,
    gremlinsPanelOpen,
    setGremlinsPanelOpen,
  } = usePanelStore(
    useShallow((state) => ({
      sceneGraphOpen: state.sceneGraphOpen,
      setSceneGraphOpen: state.actions.setSceneGraphOpen,
      filePanelOpen: state.filePanelOpen,
      setFilePanelOpen: state.actions.setFilePanelOpen,
      animationPanelOpen: state.animationPanelOpen,
      setAnimationPanelOpen: state.actions.setAnimationPanelOpen,
      materialEditorPanelOpen: state.materialEditorPanelOpen,
      setMaterialEditorPanelOpen: state.actions.setMaterialEditorPanelOpen,
      lightsPanelOpen: state.lightsPanelOpen,
      setLightsPanelOpen: state.actions.setLightsPanelOpen,
      circleDetectionOpen: state.circleDetectionOpen,
      setCircleDetectionOpen: state.actions.setCircleDetectionOpen,
      historyPanelOpen: state.historyPanelOpen,
      setHistoryPanelOpen: state.actions.setHistoryPanelOpen,
      runtimeDebugOpen: state.runtimeDebugOpen,
      setRuntimeDebugOpen: state.actions.setRuntimeDebugOpen,
      measurementsOpen: state.measurementsOpen,
      setMeasurementsOpen: state.actions.setMeasurementsOpen,
      ikTargetPanelOpen: state.ikTargetPanelOpen,
      setIkTargetPanelOpen: state.actions.setIkTargetPanelOpen,
      objectLibraryOpen: state.objectLibraryPanelOpen,
      setObjectLibraryOpen: state.actions.setObjectLibraryPanelOpen,
      actuatorControlsOpen: state.actuatorControlsOpen,
      setActuatorControlsOpen: state.actions.setActuatorControlsOpen,
      warmstartDatasetOpen: state.warmstartDatasetOpen,
      setWarmstartDatasetOpen: state.actions.setWarmstartDatasetOpen,
      behaviorEditorOpen: state.behaviorEditorOpen,
      setBehaviorEditorOpen: state.actions.setBehaviorEditorOpen,
      policyRolloutOpen: state.policyRolloutOpen,
      setPolicyRolloutOpen: state.actions.setPolicyRolloutOpen,
      trainingMetricsOpen: state.trainingMetricsOpen,
      setTrainingMetricsOpen: state.actions.setTrainingMetricsOpen,
      trainingPipelineOpen: state.trainingPipelineOpen,
      setTrainingPipelineOpen: state.actions.setTrainingPipelineOpen,
      octoTrainingOpen: state.octoTrainingOpen,
      setOctoTrainingOpen: state.actions.setOctoTrainingOpen,
      meshEditorOpen: state.meshEditorOpen,
      setMeshEditorOpen: state.actions.setMeshEditorOpen,
      sceneInventoryOpen: state.sceneInventoryOpen,
      setSceneInventoryOpen: state.actions.setSceneInventoryOpen,
      modelCheckOpen: state.modelCheckOpen,
      setModelCheckOpen: state.actions.setModelCheckOpen,
      massPropertiesOpen: state.massPropertiesOpen,
      setMassPropertiesOpen: state.actions.setMassPropertiesOpen,
      terrainPanelOpen: state.terrainPanelOpen,
      setTerrainPanelOpen: state.actions.setTerrainPanelOpen,
      xmlSourcePanelOpen: state.xmlSourcePanelOpen,
      setXmlSourcePanelOpen: state.actions.setXmlSourcePanelOpen,
      cameraProbeOpen: state.cameraProbeOpen,
      setCameraProbeOpen: state.actions.setCameraProbeOpen,
      sensorsOpen: state.sensorsOpen,
      setSensorsOpen: state.actions.setSensorsOpen,
      gremlinsPanelOpen: state.gremlinsPanelOpen,
      setGremlinsPanelOpen: state.actions.setGremlinsPanelOpen,
    }))
  );
  const authoringLocked = usePhysicsAuthoringLock();
  const authoringLockedTooltip = React.useMemo(() => getPhysicsAuthoringLockMessage("edit"), []);
  const { makeMobileExclusiveSetter } = useMainPanelMenuPanelSetterFactory({
    isMobilePanelViewport,
    closeMainPanelPopups,
  });
  const setSceneGraphOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setSceneGraphOpen),
    [makeMobileExclusiveSetter, setSceneGraphOpen]
  );
  const setFilePanelOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setFilePanelOpen),
    [makeMobileExclusiveSetter, setFilePanelOpen]
  );
  const setAnimationPanelOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setAnimationPanelOpen),
    [makeMobileExclusiveSetter, setAnimationPanelOpen]
  );
  const setMaterialEditorPanelOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setMaterialEditorPanelOpen),
    [makeMobileExclusiveSetter, setMaterialEditorPanelOpen]
  );
  const setLightsPanelOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setLightsPanelOpen),
    [makeMobileExclusiveSetter, setLightsPanelOpen]
  );
  const setCircleDetectionOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setCircleDetectionOpen),
    [makeMobileExclusiveSetter, setCircleDetectionOpen]
  );
  const setHistoryPanelOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setHistoryPanelOpen),
    [makeMobileExclusiveSetter, setHistoryPanelOpen]
  );
  const setRuntimeDebugOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setRuntimeDebugOpen),
    [makeMobileExclusiveSetter, setRuntimeDebugOpen]
  );
  const setMeasurementsOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setMeasurementsOpen),
    [makeMobileExclusiveSetter, setMeasurementsOpen]
  );
  const setIkTargetPanelOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setIkTargetPanelOpen),
    [makeMobileExclusiveSetter, setIkTargetPanelOpen]
  );
  const setObjectLibraryOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setObjectLibraryOpen),
    [makeMobileExclusiveSetter, setObjectLibraryOpen]
  );
  const setActuatorControlsOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setActuatorControlsOpen),
    [makeMobileExclusiveSetter, setActuatorControlsOpen]
  );
  const setWarmstartDatasetOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setWarmstartDatasetOpen),
    [makeMobileExclusiveSetter, setWarmstartDatasetOpen]
  );
  const setBehaviorEditorOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setBehaviorEditorOpen),
    [makeMobileExclusiveSetter, setBehaviorEditorOpen]
  );
  const setTrainingPipelineOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setTrainingPipelineOpen),
    [makeMobileExclusiveSetter, setTrainingPipelineOpen]
  );
  const setOctoTrainingOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setOctoTrainingOpen),
    [makeMobileExclusiveSetter, setOctoTrainingOpen]
  );
  const setTrainingMetricsOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setTrainingMetricsOpen),
    [makeMobileExclusiveSetter, setTrainingMetricsOpen]
  );
  const setPolicyRolloutOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setPolicyRolloutOpen),
    [makeMobileExclusiveSetter, setPolicyRolloutOpen]
  );
  const setCameraProbeOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setCameraProbeOpen),
    [makeMobileExclusiveSetter, setCameraProbeOpen]
  );
  const setSensorsOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setSensorsOpen),
    [makeMobileExclusiveSetter, setSensorsOpen]
  );
  const setMeshEditorOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setMeshEditorOpen),
    [makeMobileExclusiveSetter, setMeshEditorOpen]
  );
  const setSceneInventoryOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setSceneInventoryOpen),
    [makeMobileExclusiveSetter, setSceneInventoryOpen]
  );
  const setModelCheckOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setModelCheckOpen),
    [makeMobileExclusiveSetter, setModelCheckOpen]
  );
  const setMassPropertiesOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setMassPropertiesOpen),
    [makeMobileExclusiveSetter, setMassPropertiesOpen]
  );
  const setTerrainPanelOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setTerrainPanelOpen),
    [makeMobileExclusiveSetter, setTerrainPanelOpen]
  );
  const setXmlSourcePanelOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setXmlSourcePanelOpen),
    [makeMobileExclusiveSetter, setXmlSourcePanelOpen]
  );
  const setGremlinsPanelOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setGremlinsPanelOpen),
    [makeMobileExclusiveSetter, setGremlinsPanelOpen]
  );

  return (
    <div
      className="overflow-y-auto max-h-[calc(100vh-120px)]"
      style={{ scrollbarGutter: "stable" }}
    >
      <div className={isMobilePanelViewport ? "space-y-3 p-3" : "space-y-4 p-4"}>
        {!isMobilePanelViewport && (
          <MainPanelDesktopPhysicsSection
            onStartStop={onStartStop}
            onPauseResume={onPauseResume}
            isModelCheckOpen={modelCheckOpen}
            onOpenModelCheckPanel={() => setModelCheckOpenFromMenu(true)}
            onOpenRuntimeControlsPanel={onOpenRuntimeControlsPanel}
            onOpenPhysicsPanel={onOpenPhysicsPanel}
          />
        )}

        <SceneSection
          sceneInventoryOpen={sceneInventoryOpen}
          setSceneInventoryOpen={setSceneInventoryOpenFromMenu}
          sceneGraphOpen={sceneGraphOpen}
          setSceneGraphOpen={setSceneGraphOpenFromMenu}
        />

        <RobotModelSections
          isMobilePanelViewport={isMobilePanelViewport}
          objectLibraryOpen={objectLibraryOpen}
          setObjectLibraryOpen={setObjectLibraryOpenFromMenu}
          actuatorControlsOpen={actuatorControlsOpen}
          setActuatorControlsOpen={setActuatorControlsOpenFromMenu}
          materialEditorPanelOpen={materialEditorPanelOpen}
          setMaterialEditorPanelOpen={setMaterialEditorPanelOpenFromMenu}
          lightsPanelOpen={lightsPanelOpen}
          setLightsPanelOpen={setLightsPanelOpenFromMenu}
          cameraProbeOpen={cameraProbeOpen}
          setCameraProbeOpen={setCameraProbeOpenFromMenu}
          modelCheckOpen={modelCheckOpen}
          setModelCheckOpen={setModelCheckOpenFromMenu}
          massPropertiesOpen={massPropertiesOpen}
          setMassPropertiesOpen={setMassPropertiesOpenFromMenu}
          sensorsOpen={sensorsOpen}
          setSensorsOpen={setSensorsOpenFromMenu}
          terrainPanelOpen={terrainPanelOpen}
          setTerrainPanelOpen={setTerrainPanelOpenFromMenu}
          getPanelToggleButtonClass={getPanelToggleButtonClass}
          showDevTools={showDevTools}
          authoringLocked={authoringLocked}
          authoringLockedTooltip={authoringLockedTooltip}
        />

        <TrainingIkSection
          visible={showTrainingIkTools}
          showLeadingSeparator={showTrainingIkTools && !showDevTools}
          warmstartDatasetOpen={warmstartDatasetOpen}
          setWarmstartDatasetOpen={setWarmstartDatasetOpenFromMenu}
          behaviorEditorOpen={behaviorEditorOpen}
          setBehaviorEditorOpen={setBehaviorEditorOpenFromMenu}
          trainingPipelineOpen={trainingPipelineOpen}
          setTrainingPipelineOpen={setTrainingPipelineOpenFromMenu}
          octoTrainingOpen={octoTrainingOpen}
          setOctoTrainingOpen={setOctoTrainingOpenFromMenu}
          trainingMetricsOpen={trainingMetricsOpen}
          setTrainingMetricsOpen={setTrainingMetricsOpenFromMenu}
          policyRolloutOpen={policyRolloutOpen}
          setPolicyRolloutOpen={setPolicyRolloutOpenFromMenu}
          ikTargetPanelOpen={ikTargetPanelOpen}
          setIkTargetPanelOpen={setIkTargetPanelOpenFromMenu}
          getPanelToggleButtonClass={getPanelToggleButtonClass}
        />

        <DevToolsSection
          visible={showDevTools || showRuntimeDiagnostics}
          showFullDevTools={showDevTools}
          animationPanelOpen={animationPanelOpen}
          setAnimationPanelOpen={setAnimationPanelOpenFromMenu}
          circleDetectionOpen={circleDetectionOpen}
          setCircleDetectionOpen={setCircleDetectionOpenFromMenu}
          runtimeDebugOpen={runtimeDebugOpen}
          setRuntimeDebugOpen={setRuntimeDebugOpenFromMenu}
          measurementsOpen={measurementsOpen}
          setMeasurementsOpen={setMeasurementsOpenFromMenu}
          historyPanelOpen={historyPanelOpen}
          setHistoryPanelOpen={setHistoryPanelOpenFromMenu}
          showPerformancePanel={showPerformancePanel}
          onTogglePerformancePanel={onTogglePerformancePanel}
          filePanelOpen={filePanelOpen}
          setFilePanelOpen={setFilePanelOpenFromMenu}
          meshEditorOpen={meshEditorOpen}
          setMeshEditorOpen={setMeshEditorOpenFromMenu}
          xmlSourcePanelOpen={xmlSourcePanelOpen}
          setXmlSourcePanelOpen={setXmlSourcePanelOpenFromMenu}
          gremlinsPanelOpen={gremlinsPanelOpen}
          setGremlinsPanelOpen={setGremlinsPanelOpenFromMenu}
          getPanelToggleButtonClass={getPanelToggleButtonClass}
        />
      </div>
    </div>
  );
});

export default MainPanelContent;
